import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save, RefreshCw, Search, AlertTriangle, CheckCircle2, ExternalLink, Loader2, Settings2, Activity, Building2, MapPin, Plus, Trash2, Copy, Code2, Info, Wand2, History, Link2, FileText } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import BulkAiTools from "../../components/seo/BulkAiTools";
import LinkHealthPanel from "../../components/seo/LinkHealthPanel";
import TemplateManager from "../../components/seo/TemplateManager";

const ENTITY_TYPES = [
  { key: "product", label: "Product", tokens: "{{Product Name}}, {{Brand Name}}, {{Price}}, {{Site Name}}" },
  { key: "category", label: "Category", tokens: "{{Category Name}}, {{Site Name}}" },
  { key: "brand", label: "Brand", tokens: "{{Brand Name}}, {{Site Name}}" },
  { key: "page", label: "Static Page", tokens: "{{Page Title}}, {{Site Name}}" },
  { key: "bundle", label: "Bundle", tokens: "{{Product Name}}, {{Site Name}}" },
  { key: "homepage", label: "Homepage", tokens: "{{Site Name}}" },
  { key: "blog", label: "Blog (future)", tokens: "{{Page Title}}, {{Site Name}}" },
];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const BUSINESS_TYPES = ["Store", "LocalBusiness", "HardwareStore", "GardenStore"];
const PREVIEW_PATHS = [
  { path: "/", label: "Homepage" },
  { path: "/fireworks", label: "All Fireworks" },
  { path: "/brands", label: "Brands index" },
];

export default function AdminSeo() {
  const [settings, setSettings] = useState(null);
  const [errs, setErrs] = useState({});
  const [saving, setSaving] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [sdPath, setSdPath] = useState("/");
  const [sd, setSd] = useState(null);
  const [sdLoading, setSdLoading] = useState(false);
  const [samplePaths, setSamplePaths] = useState(PREVIEW_PATHS);
  const [aiCfg, setAiCfg] = useState(null);
  const [aiSaving, setAiSaving] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => { api.get("/admin/seo/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/seo/ai-config").then(({ data }) => setAiCfg(data)).catch(() => {}); }, []);
  useEffect(() => {
    // enrich the preview dropdown with one real product / category / brand
    Promise.all([
      api.get("/products?limit=1").then((r) => r.data.items?.[0]).catch(() => null),
      api.get("/categories").then((r) => r.data?.[0]).catch(() => null),
      api.get("/brands").then((r) => r.data?.[0]).catch(() => null),
    ]).then(([p, c, b]) => {
      const extra = [];
      if (p?.slug) extra.push({ path: `/product/${p.slug}`, label: `Product · ${p.name}` });
      if (c?.slug) extra.push({ path: `/fireworks/${c.slug}`, label: `Category · ${c.name}` });
      if (b?.slug) extra.push({ path: `/brands/${b.slug}`, label: `Brand · ${b.name}` });
      const terms = { path: "/terms", label: "Static · Terms & Conditions" };
      setSamplePaths([...PREVIEW_PATHS, ...extra, terms]);
    });
  }, []);

  const loadHealth = () => { setHealthLoading(true); api.get("/admin/seo/health").then(({ data }) => setHealth(data)).catch(() => {}).finally(() => setHealthLoading(false)); };
  const loadSd = (path) => { setSdLoading(true); api.get(`/admin/seo/structured-preview?path=${encodeURIComponent(path)}`).then(({ data }) => setSd(data)).catch(() => {}).finally(() => setSdLoading(false)); };
  const loadAudit = () => { setAuditLoading(true); api.get("/admin/seo/ai-audit?limit=100").then(({ data }) => setAudit(data.logs || [])).catch(() => {}).finally(() => setAuditLoading(false)); };

  const saveAiCfg = async () => {
    setAiSaving(true);
    try {
      const { data } = await api.put("/admin/seo/ai-config", aiCfg);
      setAiCfg(data);
      toast.success("AI settings saved.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setAiSaving(false); }
  };
  const setAi = (patch) => setAiCfg((c) => ({ ...c, ...patch }));
  const setAiModel = (patch) => setAiCfg((c) => ({ ...c, model: { ...(c.model || {}), ...patch } }));

  const save = async () => {
    setSaving(true); setErrs({});
    try {
      const { data } = await api.put("/admin/seo/settings", settings);
      setSettings(data);
      toast.success("SEO settings saved. Applies to all automatic SEO across the site.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.errors) {
        const map = {};
        detail.errors.forEach((er) => { map[er.field] = er.message; });
        setErrs(map);
        toast.error(`${detail.errors.length} field(s) need attention — see the highlighted errors.`);
      } else toast.error(formatApiError(detail));
    } finally { setSaving(false); }
  };

  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const setTpl = (type, field, v) => setSettings((s) => ({ ...s, templates: { ...s.templates, [type]: { ...(s.templates?.[type] || {}), [field]: v } } }));
  const setOrg = (patch) => setSettings((s) => ({ ...s, organisation: { ...(s.organisation || {}), ...patch } }));
  const setLb = (patch) => setSettings((s) => ({ ...s, local_business: { ...(s.local_business || {}), ...patch } }));

  if (!settings) return <div className="p-8 text-white/50">Loading…</div>;
  const org = settings.organisation || {};
  const lb = settings.local_business || {};

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="admin-seo-page">
      <div>
        <h1 className="text-2xl font-cinzel text-gold">SEO Management</h1>
        <p className="text-sm text-white/60">Shopify-style automatic SEO + structured data for every product, category, brand and page.</p>
      </div>

      <Tabs defaultValue="settings">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="settings" data-testid="seo-tab-settings" className="gap-2"><Settings2 className="h-4 w-4" /> Global Settings</TabsTrigger>
          <TabsTrigger value="structured" data-testid="seo-tab-structured" onClick={() => loadSd(sdPath)} className="gap-2"><Code2 className="h-4 w-4" /> Structured Data</TabsTrigger>
          <TabsTrigger value="health" data-testid="seo-tab-health" onClick={loadHealth} className="gap-2"><Activity className="h-4 w-4" /> Health Monitor</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="seo-tab-bulk" className="gap-2"><Wand2 className="h-4 w-4" /> Bulk AI Tools</TabsTrigger>
          <TabsTrigger value="links" data-testid="seo-tab-links" className="gap-2"><Link2 className="h-4 w-4" /> Internal Links</TabsTrigger>
          <TabsTrigger value="templates" data-testid="seo-tab-templates" className="gap-2"><FileText className="h-4 w-4" /> Templates</TabsTrigger>
          <TabsTrigger value="audit" data-testid="seo-tab-audit" onClick={loadAudit} className="gap-2"><History className="h-4 w-4" /> AI Audit Log</TabsTrigger>
        </TabsList>

        {/* ---- Global Settings ---- */}
        <TabsContent value="settings" className="space-y-6 mt-6">
          <Section title="Website Defaults">
            <div className="grid md:grid-cols-2 gap-4">
              <F label="Website Name"><Input value={settings.site_name || ""} onChange={(e) => set({ site_name: e.target.value })} data-testid="seo-site-name" /></F>
              <F label="Canonical Domain" hint="e.g. https://www.clactonfireworks.co.uk (blank = current domain)" error={errs.canonical_domain}>
                <Input value={settings.canonical_domain || ""} onChange={(e) => set({ canonical_domain: e.target.value })} data-testid="seo-canonical-domain" />
              </F>
              <F label="Default Open Graph Image URL"><Input value={settings.default_og_image || ""} onChange={(e) => set({ default_og_image: e.target.value })} data-testid="seo-default-og" /></F>
              <F label="Default Twitter / X Image URL"><Input value={settings.default_twitter_image || ""} onChange={(e) => set({ default_twitter_image: e.target.value })} /></F>
              <F label="Twitter / X Handle"><Input value={settings.twitter_handle || ""} onChange={(e) => set({ twitter_handle: e.target.value })} placeholder="@clactonfireworks" /></F>
            </div>
            <div className="flex gap-8 pt-2">
              <label className="flex items-center gap-2 text-sm"><Switch checked={settings.default_index !== false} onCheckedChange={(v) => set({ default_index: v })} data-testid="seo-default-index" /> Index new pages by default</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={settings.default_follow !== false} onCheckedChange={(v) => set({ default_follow: v })} /> Follow links by default</label>
            </div>
          </Section>

          {/* Organisation */}
          <Section title="Organisation (Structured Data)" subtitle="Used site-wide as a single Organisation entity ({{domain}}/#organisation).">
            <div className="grid md:grid-cols-2 gap-4">
              <F label="Organisation Name" error={errs["organisation.name"]}><Input value={org.name || ""} onChange={(e) => setOrg({ name: e.target.value })} data-testid="org-name" /></F>
              <F label="Legal / Trading Name"><Input value={org.legal_name || ""} onChange={(e) => setOrg({ legal_name: e.target.value })} /></F>
              <F label="Logo URL (absolute)"><Input value={org.logo || ""} onChange={(e) => setOrg({ logo: e.target.value })} data-testid="org-logo" /></F>
              <F label="Contact Email" error={errs["organisation.email"]}><Input value={org.email || ""} onChange={(e) => setOrg({ email: e.target.value })} data-testid="org-email" /></F>
              <F label="Contact Telephone" error={errs["organisation.telephone"]}><Input value={org.telephone || ""} onChange={(e) => setOrg({ telephone: e.target.value })} data-testid="org-phone" /></F>
            </div>
            <UrlList label="Social Media Profile URLs" values={org.social_profiles || []} onChange={(v) => setOrg({ social_profiles: v })} testid="org-socials" errs={errs} prefix="organisation.social_profiles" />
          </Section>

          {/* LocalBusiness */}
          <Section title="LocalBusiness (Structured Data)" subtitle="Adds LocalBusiness to the homepage graph ({{domain}}/#localbusiness). Only completed fields are published.">
            <label className="flex items-center gap-2 text-sm mb-4"><Switch checked={!!lb.enabled} onCheckedChange={(v) => setLb({ enabled: v })} data-testid="lb-enabled" /> Enable LocalBusiness structured data</label>
            {lb.enabled && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <F label="Business Name" error={errs["local_business.name"]}><Input value={lb.name || ""} onChange={(e) => setLb({ name: e.target.value })} data-testid="lb-name" /></F>
                  <F label="Business Type (Schema subtype)">
                    <select className="cf-select w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm" value={lb.business_type || "Store"} onChange={(e) => setLb({ business_type: e.target.value })} data-testid="lb-type">
                      {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </F>
                  <F label="Street Address"><Input value={lb.street_address || ""} onChange={(e) => setLb({ street_address: e.target.value })} data-testid="lb-street" /></F>
                  <F label="Town / City" error={errs["local_business.locality"]}><Input value={lb.locality || ""} onChange={(e) => setLb({ locality: e.target.value })} data-testid="lb-locality" /></F>
                  <F label="County / Region"><Input value={lb.region || ""} onChange={(e) => setLb({ region: e.target.value })} /></F>
                  <F label="Postcode" error={errs["local_business.postcode"]}><Input value={lb.postcode || ""} onChange={(e) => setLb({ postcode: e.target.value })} data-testid="lb-postcode" /></F>
                  <F label="Country (ISO)"><Input value={lb.country || ""} onChange={(e) => setLb({ country: e.target.value })} placeholder="GB" /></F>
                  <F label="Telephone" error={errs["local_business.telephone"]}><Input value={lb.telephone || ""} onChange={(e) => setLb({ telephone: e.target.value })} /></F>
                  <F label="Email" error={errs["local_business.email"]}><Input value={lb.email || ""} onChange={(e) => setLb({ email: e.target.value })} /></F>
                  <F label="Price Range"><Input value={lb.price_range || ""} onChange={(e) => setLb({ price_range: e.target.value })} placeholder="££" /></F>
                  <F label="Business Logo URL"><Input value={lb.logo || ""} onChange={(e) => setLb({ logo: e.target.value })} /></F>
                  <F label="Business Image URL"><Input value={lb.image || ""} onChange={(e) => setLb({ image: e.target.value })} /></F>
                  <F label="Google Maps URL" error={errs["local_business.google_maps_url"]}><Input value={lb.google_maps_url || ""} onChange={(e) => setLb({ google_maps_url: e.target.value })} /></F>
                  <F label="Latitude" error={errs["local_business.latitude"] || errs["local_business.geo"]}><Input value={lb.latitude || ""} onChange={(e) => setLb({ latitude: e.target.value })} data-testid="lb-lat" placeholder="51.7912" /></F>
                  <F label="Longitude" error={errs["local_business.longitude"]}><Input value={lb.longitude || ""} onChange={(e) => setLb({ longitude: e.target.value })} data-testid="lb-lng" placeholder="1.1552" /></F>
                </div>
                <OpeningHours value={lb.opening_hours || []} onChange={(v) => setLb({ opening_hours: v })} errs={errs} />
                <p className="text-[11px] text-white/40 flex items-center gap-1"><Info className="h-3 w-3" /> Do not enter ratings/reviews here — they are only published when genuine review data exists.</p>
              </div>
            )}
          </Section>

          <Section title="SEO Templates" subtitle="Use placeholders — they are replaced automatically for each item.">
            <div className="space-y-5">
              {ENTITY_TYPES.map((et) => (
                <div key={et.key} className="rounded-lg border border-white/10 p-4 bg-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-sm">{et.label}</p>
                    <p className="text-[11px] text-white/40">Tokens: {et.tokens}</p>
                  </div>
                  <div className="space-y-3">
                    <F label="Title Template"><Input value={settings.templates?.[et.key]?.title || ""} onChange={(e) => setTpl(et.key, "title", e.target.value)} data-testid={`seo-tpl-${et.key}-title`} /></F>
                    <F label="Description Template"><Textarea rows={2} value={settings.templates?.[et.key]?.description || ""} onChange={(e) => setTpl(et.key, "description", e.target.value)} data-testid={`seo-tpl-${et.key}-desc`} /></F>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Technical">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/sitemap.xml`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-gold hover:underline" data-testid="seo-sitemap-link"><ExternalLink className="h-4 w-4" /> View sitemap.xml</a>
              <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/robots.txt`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-gold hover:underline" data-testid="seo-robots-link"><ExternalLink className="h-4 w-4" /> View robots.txt</a>
            </div>
          </Section>

          {/* AI Content Assistant config (model + prompts — no hardcoding) */}
          {aiCfg && (
            <Section title="AI Content Assistant" subtitle="Model and prompt templates for AI generation. Change these to tune the assistant without any code change.">
              <div className="grid md:grid-cols-2 gap-4">
                <F label="AI Provider">
                  <select className="cf-select w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm" value={aiCfg.model?.provider || "openai"} onChange={(e) => setAiModel({ provider: e.target.value })} data-testid="ai-provider">
                    {["openai", "anthropic", "gemini"].map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </F>
                <F label="Model Name" hint="e.g. gpt-5.4, claude-sonnet-4-6, gemini-3-flash"><Input value={aiCfg.model?.name || ""} onChange={(e) => setAiModel({ name: e.target.value })} data-testid="ai-model-name" /></F>
              </div>
              <div className="space-y-3 mt-3">
                <F label="System Prompt (uses {site} and {format_rule})"><Textarea rows={4} value={aiCfg.system_base || ""} onChange={(e) => setAi({ system_base: e.target.value })} data-testid="ai-system-prompt" className="font-mono text-xs" /></F>
                <F label="FAQ Prompt (uses {entity_type} and {verified})"><Textarea rows={3} value={aiCfg.faq_prompt || ""} onChange={(e) => setAi({ faq_prompt: e.target.value })} className="font-mono text-xs" /></F>
                <F label="Metadata Prompt (uses {site})"><Textarea rows={3} value={aiCfg.meta_system || ""} onChange={(e) => setAi({ meta_system: e.target.value })} className="font-mono text-xs" /></F>
              </div>
              <p className="text-[11px] text-amber-400 flex items-center gap-1 mt-2"><Info className="h-3 w-3" /> Accuracy guardrails (never invent specs/prices/safety data) are always enforced regardless of prompt edits.</p>
              <Button className="cf-gold-btn gap-2 mt-3" onClick={saveAiCfg} disabled={aiSaving} data-testid="ai-save-config">
                {aiSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save AI Settings
              </Button>
            </Section>
          )}

          <Button className="cf-gold-btn gap-2" onClick={save} disabled={saving} data-testid="seo-save-settings">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings
          </Button>
        </TabsContent>

        {/* ---- Structured Data Preview ---- */}
        <TabsContent value="structured" className="space-y-5 mt-6">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-sm">Preview page:</Label>
            <select className="cf-select bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm" value={sdPath} onChange={(e) => { setSdPath(e.target.value); loadSd(e.target.value); }} data-testid="sd-path-select">
              {samplePaths.map((p) => <option key={p.path} value={p.path}>{p.label}</option>)}
            </select>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => loadSd(sdPath)} data-testid="sd-refresh"><RefreshCw className="h-4 w-4" /> Refresh</Button>
          </div>

          {sdLoading && <p className="text-white/40 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Generating…</p>}
          {sd && !sdLoading && (
            <>
              <div className="flex flex-wrap gap-2" data-testid="sd-types">
                {sd.types.map((t, i) => <span key={i} className="text-xs rounded-full bg-gold/15 text-gold px-3 py-1">{t}</span>)}
              </div>
              <IssueList title="Errors" items={sd.errors} tone="error" icon={AlertTriangle} testid="sd-errors" empty="No errors." />
              <IssueList title="Warnings" items={sd.warnings} tone="warn" icon={AlertTriangle} testid="sd-warnings" empty="No warnings." />
              <IssueList title="Recommendations" items={sd.recommendations} tone="rec" icon={Info} testid="sd-recs" empty="Nothing recommended." />
              <div className="rounded-lg border border-white/10 bg-black/40 overflow-hidden" data-testid="sd-jsonld">
                <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
                  <span className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-2"><Code2 className="h-4 w-4" /> JSON-LD (@graph)</span>
                  <Button size="sm" variant="ghost" className="gap-1 text-gold" onClick={() => { navigator.clipboard?.writeText(JSON.stringify(sd.graph, null, 2)); toast.success("JSON-LD copied"); }} data-testid="sd-copy"><Copy className="h-3.5 w-3.5" /> Copy</Button>
                </div>
                <pre className="p-4 text-[11px] text-white/70 overflow-x-auto max-h-[420px]">{JSON.stringify(sd.graph, null, 2)}</pre>
              </div>
            </>
          )}
        </TabsContent>

        {/* ---- Health Monitor ---- */}
        <TabsContent value="health" className="space-y-6 mt-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-white/60">Scans all products, categories, brands and pages for SEO issues.</p>
            <Button variant="outline" className="gap-2" onClick={loadHealth} disabled={healthLoading} data-testid="seo-health-refresh">
              {healthLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Rescan
            </Button>
          </div>
          {!health && !healthLoading && <p className="text-white/40 text-sm">Click "Rescan" to run the SEO health check.</p>}
          {healthLoading && <p className="text-white/40 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Scanning…</p>}
          {health && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Pages Scanned" value={health.summary.scanned} icon={Search} />
                <Stat label="Pages w/ Issues" value={health.summary.issues} icon={AlertTriangle} tone={health.summary.issues ? "warn" : "ok"} />
                <Stat label="Duplicate Titles" value={health.summary.duplicate_titles} icon={AlertTriangle} tone={health.summary.duplicate_titles ? "warn" : "ok"} />
                <Stat label="Avg Score (issues)" value={health.summary.avg_score} icon={CheckCircle2} />
              </div>
              {health.duplicate_titles?.length > 0 && (
                <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4">
                  <p className="font-medium text-sm mb-2 text-yellow-400">Duplicate SEO Titles</p>
                  {health.duplicate_titles.map((d, i) => <p key={i} className="text-xs text-white/70">"{d.title}" — {d.items.join(", ")}</p>)}
                </div>
              )}
              <div className="space-y-3">
                {health.issues.length === 0 ? (
                  <div className="flex items-center gap-2 text-green-400"><CheckCircle2 className="h-5 w-5" /> No SEO issues found. Great work!</div>
                ) : health.issues.map((it, i) => (
                  <div key={i} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`seo-issue-${i}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <ScoreBadge score={it.score} />
                        <div><p className="text-sm font-medium">{it.label}</p><p className="text-[11px] uppercase tracking-wider text-white/40">{it.entity_type}</p></div>
                      </div>
                      <Link to={it.edit_path} className="text-gold text-xs hover:underline flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Fix</Link>
                    </div>
                    <ul className="mt-2 space-y-1">
                      {it.problems.map((p, j) => (
                        <li key={j} className="text-xs text-white/60 flex items-start gap-2"><AlertTriangle className={`h-3.5 w-3.5 shrink-0 ${p.status === "warn" ? "text-yellow-500" : "text-red-500"}`} /><span><b className="text-white/75">{p.label}:</b> {p.message}</span></li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* ---- Bulk AI Tools ---- */}
        <TabsContent value="bulk" className="mt-6">
          <BulkAiTools />
        </TabsContent>

        {/* ---- Internal Links health ---- */}
        <TabsContent value="links" className="mt-6">
          <LinkHealthPanel />
        </TabsContent>

        {/* ---- Content Templates ---- */}
        <TabsContent value="templates" className="mt-6">
          <TemplateManager />
        </TabsContent>

        {/* ---- AI Audit Log ---- */}
        <TabsContent value="audit" className="space-y-4 mt-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-white/60">Every AI generation, approval and rejection is recorded here.</p>
            <Button variant="outline" className="gap-2" onClick={loadAudit} disabled={auditLoading} data-testid="ai-audit-refresh">
              {auditLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </Button>
          </div>
          {!audit && !auditLoading && <p className="text-white/40 text-sm">Click "Refresh" to load the AI audit log.</p>}
          {audit && audit.length === 0 && <p className="text-white/40 text-sm" data-testid="ai-audit-empty">No AI activity recorded yet.</p>}
          {audit && audit.length > 0 && (
            <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="ai-audit-table">
              <div className="grid grid-cols-[auto_1fr_1fr_1fr_1fr] gap-3 px-3 py-2 text-[11px] uppercase tracking-wider text-white/40 border-b border-white/10 bg-white/5">
                <span>When</span><span>Action</span><span>Item</span><span>Content</span><span>Model / By</span>
              </div>
              {audit.map((l) => (
                <div key={l.audit_id} className="grid grid-cols-[auto_1fr_1fr_1fr_1fr] gap-3 px-3 py-2 items-center border-b border-white/5 text-xs" data-testid={`ai-audit-row-${l.audit_id}`}>
                  <span className="text-white/45 whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  <span><span className={`rounded-full px-2 py-0.5 text-[10px] ${l.action === "approve" ? "bg-green-500/20 text-green-300" : l.action === "reject" ? "bg-white/10 text-white/50" : "bg-gold/15 text-gold"}`}>{l.action}</span></span>
                  <span className="text-white/80 truncate">{l.entity_name || l.entity_type || "—"}</span>
                  <span className="text-white/60 truncate">{l.content_type || "—"}</span>
                  <span className="text-white/50 truncate">{l.model || "—"}<br /><span className="text-white/35">{l.actor}</span></span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (<div className="rounded-xl border border-white/10 bg-black/20 p-5"><h2 className="text-lg font-cinzel text-white mb-1">{title}</h2>{subtitle && <p className="text-xs text-white/50 mb-4">{subtitle}</p>}<div className={subtitle ? "" : "mt-3"}>{children}</div></div>);
}
function F({ label, hint, error, children }) {
  return (<div><Label className="text-xs uppercase tracking-wider text-white/60">{label}</Label>{children}{error ? <p className="text-[11px] text-red-400 mt-1" data-testid="field-error">{error}</p> : hint ? <p className="text-[11px] text-white/40 mt-1">{hint}</p> : null}</div>);
}
function Stat({ label, value, icon: Icon, tone }) {
  const color = tone === "warn" ? "text-yellow-400" : tone === "ok" ? "text-green-400" : "text-white";
  return (<div className="rounded-lg border border-white/10 bg-white/5 p-4"><div className="flex items-center gap-2 text-white/50 text-xs"><Icon className="h-4 w-4" /> {label}</div><p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p></div>);
}
function ScoreBadge({ score }) {
  const color = score >= 80 ? "bg-green-500/20 text-green-400" : score >= 50 ? "bg-yellow-500/20 text-yellow-400" : "bg-red-500/20 text-red-400";
  return <span className={`text-sm font-bold rounded-full h-10 w-10 grid place-items-center ${color}`}>{score}</span>;
}
function UrlList({ label, values, onChange, testid, errs = {}, prefix = "" }) {
  const upd = (i, v) => onChange(values.map((x, j) => (j === i ? v : x)));
  return (
    <div className="mt-4" data-testid={testid}>
      <Label className="text-xs uppercase tracking-wider text-white/60">{label}</Label>
      <div className="space-y-2 mt-1">
        {values.map((v, i) => (
          <div key={i}>
            <div className="flex gap-2">
              <Input value={v} onChange={(e) => upd(i, e.target.value)} placeholder="https://facebook.com/…" />
              <Button type="button" variant="ghost" size="icon" className="text-red-400" onClick={() => onChange(values.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
            </div>
            {errs[`${prefix}[${i}]`] && <p className="text-[11px] text-red-400 mt-1">{errs[`${prefix}[${i}]`]}</p>}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => onChange([...values, ""])} data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5" /> Add profile</Button>
      </div>
    </div>
  );
}
function OpeningHours({ value, onChange, errs = {} }) {
  const upd = (i, patch) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const toggleDay = (i, day) => { const r = value[i]; const days = r.days || []; upd(i, { days: days.includes(day) ? days.filter((d) => d !== day) : [...days, day] }); };
  return (
    <div data-testid="lb-hours">
      <Label className="text-xs uppercase tracking-wider text-white/60">Opening Hours</Label>
      <p className="text-[11px] text-white/40 mb-2">Only add hours once verified. Leave empty until confirmed. For seasonal operation (e.g. the licensed fireworks selling period) set an optional valid-from / valid-to date range.</p>
      <div className="space-y-3">
        {value.map((r, i) => (
          <div key={i} className="rounded-lg border border-white/10 p-3 bg-white/5 space-y-2">
            <div className="flex flex-wrap gap-1">
              {DAYS.map((d) => (
                <button key={d} type="button" onClick={() => toggleDay(i, d)} className={`text-[11px] px-2 py-1 rounded-md border ${(r.days || []).includes(d) ? "bg-gold/20 text-gold border-gold/40" : "border-white/15 text-white/60"}`}>{d.slice(0, 3)}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input type="time" value={r.opens || ""} onChange={(e) => upd(i, { opens: e.target.value })} className="w-32" data-testid={`lb-hours-opens-${i}`} />
              <span className="text-white/40 text-sm">to</span>
              <Input type="time" value={r.closes || ""} onChange={(e) => upd(i, { closes: e.target.value })} className="w-32" data-testid={`lb-hours-closes-${i}`} />
              <Button type="button" variant="ghost" size="icon" className="text-red-400 ml-auto" onClick={() => onChange(value.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-white/40">Seasonal (optional):</span>
              <Input type="date" value={r.valid_from || ""} onChange={(e) => upd(i, { valid_from: e.target.value })} className="w-40" data-testid={`lb-hours-validfrom-${i}`} />
              <span className="text-white/40 text-sm">to</span>
              <Input type="date" value={r.valid_through || ""} onChange={(e) => upd(i, { valid_through: e.target.value })} className="w-40" data-testid={`lb-hours-validthrough-${i}`} />
            </div>
            {(errs[`local_business.opening_hours[${i}].days`] || errs[`local_business.opening_hours[${i}].opens`] || errs[`local_business.opening_hours[${i}].closes`] || errs[`local_business.opening_hours[${i}].valid_from`] || errs[`local_business.opening_hours[${i}].valid_through`]) && (
              <p className="text-[11px] text-red-400">{errs[`local_business.opening_hours[${i}].days`] || errs[`local_business.opening_hours[${i}].opens`] || errs[`local_business.opening_hours[${i}].closes`] || errs[`local_business.opening_hours[${i}].valid_from`] || errs[`local_business.opening_hours[${i}].valid_through`]}</p>
            )}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => onChange([...value, { days: [], opens: "", closes: "", valid_from: "", valid_through: "" }])} data-testid="lb-hours-add"><Plus className="h-3.5 w-3.5" /> Add hours</Button>
      </div>
    </div>
  );
}
function IssueList({ title, items, tone, icon: Icon, testid, empty }) {
  const color = tone === "error" ? "text-red-400 border-red-500/30 bg-red-500/5" : tone === "warn" ? "text-yellow-400 border-yellow-500/30 bg-yellow-500/5" : "text-sky-300 border-sky-500/30 bg-sky-500/5";
  return (
    <div className={`rounded-lg border p-4 ${color.split(" ").slice(1).join(" ")}`} data-testid={testid}>
      <p className={`text-sm font-medium mb-2 ${color.split(" ")[0]}`}>{title} ({items?.length || 0})</p>
      {(!items || items.length === 0) ? <p className="text-xs text-white/40">{empty}</p> : (
        <ul className="space-y-1">{items.map((m, i) => <li key={i} className="text-xs text-white/70 flex items-start gap-2"><Icon className={`h-3.5 w-3.5 shrink-0 ${color.split(" ")[0]}`} /> {m}</li>)}</ul>
      )}
    </div>
  );
}
